import React, { useState, useEffect, useRef } from "react";
import { MODULES, getAccountsUser } from "../services/modulesApi";
import ChangePasswordModal from "./auth/ChangePasswordModal"; // 2026-10-07 accounts
import { refreshPoCache } from "../services/api";
import ModuleIcon, { BrandMark } from "./ModuleIcon";
import { isViewOnly } from "../utils/access";
import { useStyleProfitPref } from "../utils/stylePrefs"; // 2026-10-26 per-person Style profit search on/off
import { Switch } from "./common/ui";
import { useToolOrder } from "../utils/toolOrder"; // 2026-10-26 per-person tool order

// Theme: saved choice, else the OS preference. Exported so index.js applies it before first paint (sign-in screens included).
export function readTheme() {
  try {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark" ? "dark" : "light";
  } catch (e) { /* storage blocked - fall back to the OS preference */ }
  try { return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"; } catch (e) { return "light"; }
}
export function applyTheme(theme) {
  try { document.documentElement.setAttribute("data-theme", theme === "dark" ? "dark" : "light"); } catch (e) { console.error("could not apply theme", e); }
}

const svgP = { className: "oh-ico", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" };
const IconGrid = () => (<svg {...svgP}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>);
const IconKey = () => (<svg {...svgP}><circle cx="8" cy="15" r="4" /><path d="M10.8 12.2L20 3M16 7l3 3" /></svg>);
const IconGear = () => (<svg {...svgP}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>);
const IconLogout = () => (<svg {...svgP}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></svg>);
const IconSun = () => (<svg {...svgP}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>);
const IconMoon = () => (<svg {...svgP}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>);
const IconReload = () => (<svg {...svgP} style={{ width: 13, height: 13 }}><path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" /></svg>);
const IconChevron = () => (<svg {...svgP} style={{ width: 12, height: 12 }}><path d="m6 9 6 6 6-6" /></svg>);

// Closes a popover on outside click / Escape.
function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, setOpen]);
  return ref;
}

function ModuleSwitcher({ module, modules, onSwitch }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  const cur = MODULES[module];
  const [order] = useToolOrder();
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="oh-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Switch tool" title="Switch tool">
        {cur ? <ModuleIcon id={module} size={16} /> : <IconGrid />}<span className="oh-hide-sm">Switch tool</span><IconChevron />
      </button>
      {open && (
        <div role="menu" className="oh-menu" style={{ minWidth: 260 }}>
          {order.filter((m) => modules.includes(m)).map((m) => (
            <button key={m} role="menuitem" className={`oh-menu-item${m === module ? " on" : ""}`}
              onClick={() => { setOpen(false); onSwitch(m); }}>
              <span style={{ color: m === module ? "var(--accent)" : "var(--text-muted)", display: "inline-flex" }}><ModuleIcon id={m} size={18} /></span>
              <span style={{ flex: 1 }}>{MODULES[m].title}</span>
              {m === module && <span style={{ color: "var(--accent)" }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Light / dark switch: two-option segmented control so the current mode is always visible.
function ThemeToggle({ dark, onChange }) {
  return (
    <div className="oh-theme" role="radiogroup" aria-label="Colour theme">
      <button type="button" role="radio" aria-checked={!dark} className={!dark ? "on" : ""} onClick={() => onChange(false)} title="Light mode"><IconSun /></button>
      <button type="button" role="radio" aria-checked={dark} className={dark ? "on" : ""} onClick={() => onChange(true)} title="Dark mode"><IconMoon /></button>
    </div>
  );
}

function UserMenu({ name, viewOnly, stylePref, onStylePref, onChangePassword, onLogout, onOpenSettings, settingsActive, settingsLabel = "Settings" }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="oh-user" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} title={name || "Account"} aria-label="Account menu">
        <span className="oh-avatar">{(name || "?").charAt(0).toUpperCase()}</span>
        <span className="oh-hide-sm oh-user-name">{name}</span>
        <IconChevron />
      </button>
      {open && (
        <div role="menu" className="oh-menu" style={{ minWidth: 220 }}>
          <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: "var(--text)", overflowWrap: "anywhere" }}>{name || "Signed in"}</div>
            {viewOnly && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>View only — you can look but not change anything</div>}
          </div>
          {onOpenSettings && (
            <button role="menuitem" className={`oh-menu-item${settingsActive ? " on" : ""}`} onClick={() => { setOpen(false); onOpenSettings(); }}>
              <IconGear /><span style={{ flex: 1 }}>{settingsLabel}</span>
            </button>
          )}
          {onStylePref && (
            <div role="menuitem" className="oh-menu-item" style={{ cursor: "default" }} title="Show or hide the Style profit search (sidebar and Overall Summary) for you only">
              <span style={{ flex: 1 }}>Style profit search</span>
              <Switch size="sm" checked={stylePref} onChange={onStylePref} label="Style profit search" />
            </div>
          )}
          {onChangePassword && (
            <button role="menuitem" className="oh-menu-item" onClick={() => { setOpen(false); onChangePassword(); }}>
              <IconKey /><span style={{ flex: 1 }}>Change password</span>
            </button>
          )}
          {onLogout && (
            <button role="menuitem" className="oh-menu-item danger" onClick={() => { setOpen(false); onLogout(); }}>
              <IconLogout /><span style={{ flex: 1 }}>Log out</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// 2026-10-09: onOpenSettings (admins only, decided in App.js) adds "Settings" to the user menu; `title` names an
// app-level page that is not a tool (the Settings view: breadcrumb "SochOne / Settings").
// 2026-10-13: settingsLabel "Team" = a team lead's own Team page (same menu slot; App.js decides who gets it).
export default function Header({ poStatus, onRefreshed, showToast, module = "reorder", modules = [], onSwitchModule, onHome, onLogout, onOpenSettings, title, settingsLabel }) {
  const [refreshing, setRefreshing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [dark, setDark] = useState(() => readTheme() === "dark");
  const [stylePref, setStylePref] = useStyleProfitPref(showToast);

  const setTheme = (next) => {
    setDark(next);
    applyTheme(next ? "dark" : "light");
    try { localStorage.setItem("theme", next ? "dark" : "light"); } catch (e) { console.error("could not save theme", e); }
  };

  useEffect(() => { applyTheme(dark ? "dark" : "light"); }, [dark]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPoCache();
      if (onRefreshed) onRefreshed();
      if (showToast) showToast("PO file reloaded", "success");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to refresh PO file";
      if (showToast) showToast(msg, "error");
    } finally {
      setRefreshing(false);
    }
  };

  const acctUser = getAccountsUser(); // 2026-10-07 accounts: name / view-only from the signed-in user
  const cur = module ? MODULES[module] : null;
  const multi = modules.length > 1;
  const name = (acctUser && acctUser.name) || localStorage.getItem("reorder_display_name") || localStorage.getItem("reorder_buyer") || "";
  const viewOnly = isViewOnly(acctUser, module); // 2026-10-13: per tool (effective tool rights); launcher = View on every tool

  return (
    <div className="header">
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} showToast={showToast} />}
      <div className="header-title">
        <button className="oh-logo" onClick={onHome} title={multi ? "Back to all tools" : "Home"} aria-label={multi ? "Back to all tools" : "Home"}>
          <BrandMark size={28} />
          <span className="oh-brand oh-hide-sm">SochOne</span>
        </button>
        <span className="oh-sep oh-hide-sm" aria-hidden="true">/</span>
        <span className="oh-title" title={cur ? cur.title : (title || "SochOne Tools")}>
          {cur ? <><span className="oh-hide-sm" style={{ display: "inline-flex", color: "var(--text-muted)" }}><ModuleIcon id={module} size={17} /></span>{cur.title}</>
            : title ? <><span className="oh-hide-sm" style={{ display: "inline-flex", color: "var(--text-muted)" }}><IconGear /></span>{title}</> : "All tools"}
        </span>
      </div>
      <div className="oh-right">
        {module === "reorder" && (
          <div className="header-po-badge" title={poStatus?.available ? `PO file: ${poStatus.file}${poStatus.cached_at ? ` · cached ${poStatus.cached_at}` : ""}` : "PO file not found"}>
            <div className={`po-dot ${poStatus?.available ? "" : "offline"}`} />
            {poStatus?.available ? (
              <>
                <span className="oh-hide-sm" style={{ color: "var(--text-muted)" }}>PO</span>
                <strong className="po-file">{poStatus.file}</strong>
                {poStatus.cached_at && (
                  <span className="oh-hide-sm" style={{ color: "var(--text-muted)" }}>· cached {poStatus.cached_at}</span>
                )}
                <button
                  className="btn-refresh"
                  title="Force reload PO file from disk"
                  aria-label="Reload PO file"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? "…" : <IconReload />}
                </button>
              </>
            ) : (
              <span>PO file not found</span>
            )}
          </div>
        )}
        {multi && module && (
          <button className="oh-btn" onClick={onHome} aria-label="All tools" title="All tools"><IconGrid /><span className="oh-hide-sm">Tools</span></button>
        )}
        {multi && module && <ModuleSwitcher module={module} modules={modules} onSwitch={onSwitchModule} />}
        {viewOnly && (
          <span className="oh-hide-sm" title="You can look but not change anything" style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: "var(--bg-hover)", color: "var(--text-muted)", whiteSpace: "nowrap" }}>View only</span>
        )}
        <ThemeToggle dark={dark} onChange={setTheme} />
        <UserMenu name={name} viewOnly={viewOnly} stylePref={stylePref} onStylePref={acctUser ? setStylePref : null} onLogout={onLogout} onOpenSettings={onOpenSettings} settingsActive={!!title} settingsLabel={settingsLabel}
          onChangePassword={() => setShowChangePassword(true)} />
      </div>
    </div>
  );
}
